import { useInfiniteQuery } from "@tanstack/react-query";
import { cityService } from "../api/services/cityService";
import type {
  CitiesResponse,
  City,
  CityFilters,
} from "../schemas/citySchema";
import { useCities } from "./useCities";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type CitiesInfiniteFilters = Omit<CityFilters, "page" | "pageSize"> & {
  pageSize?: number;
};

export const useCitiesInfinite = (filters: CitiesInfiniteFilters = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useCities(undefined, { enabled: false });

  const query = useInfiniteQuery<CitiesResponse, Error>({
    queryKey: ["cities", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      cityService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const cities: City[] = query.data?.pages.flatMap((p) => p.cities) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    cities,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
